<script setup>
import { resolveComponent, computed, ref, watch, defineAsyncComponent, onMounted } from 'vue'
import { useElementSize } from '@vueuse/core'

import AppLoaderPaw from '@dogfy/components/layout/AppLoaderPaw.vue'
import BaseIcon from '@dogfy/components/base/BaseIcon.vue'

import { toTitleCase } from '@dogfy/lib/utils'

const props = defineProps({
  tag: {
    type: String,
    default: 'button',
  },
  type: {
    type: String,
    default: null,
  },
  skin: {
    type: String,
    default: null,
  },
  nativeType: {
    type: String,
    default: 'button',
  },
  to: {
    type: [String, Object],
    default: null,
  },
  href: {
    type: String,
    default: null,
  },
  target: {
    type: String,
    default: null,
  },
  rel: {
    type: String,
    default: null,
  },
  icon: {
    type: String,
    default: null,
  },
  iconSize: {
    type: String,
    default: 'medium',
  },
  loadingOverlayOn: {
    type: String,
    default: 'body',
  },
  hasIconLeft: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
})

const buttonRef = ref(null)
const { width: buttonWidth } = useElementSize(buttonRef)
const loadingButtonWidth = ref(0)
const isMounted = ref(false)

const classModifiers = computed(() => props.type ? props.type.split(' ').map(e => `button--${e}`) : '')

const componentType = props.tag === 'NuxtLink'
  ? resolveComponent('NuxtLink')
  : props.tag === 'RouterLink' || props.tag === 'router-link'
    ? resolveComponent('RouterLink')
    : props.tag

const componentProps = computed(() =>
  props.tag === 'NuxtLink' || props.tag === 'RouterLink' || props.tag === 'router-link'
    ? { to: props.to, rel: props.rel }
    : props.tag === 'a'
      ? { href: props.href, rel: props.rel }
      : { type: props.nativeType },
)

// Dynamically load the skin component based on the 'skin' prop.
const skinComponent = computed(() => props.skin ? defineAsyncComponent(() => import(`../ButtonSkin${toTitleCase(props.skin)}.vue`).catch(e => null)) : null)

watch(() => props.isLoading, isLoading => {
  if (isLoading) {
    loadingButtonWidth.value = buttonWidth.value
  }
})

onMounted(() => {
  isMounted.value = true
})
</script>

<template>
  <component
    :is="componentType"
    v-bind="componentProps"
    ref="buttonRef"
    class="base-button button"
    :class="[
      ...classModifiers,
      { 'base-button--overlayed': props.isLoading },
    ]"
    :target="props.target"
  >
    <AppLoaderPaw v-if="props.isLoading" class="base-button__loader" role="presentation" />
    <template v-if="!props.isLoading">
      <BaseIcon v-if="props.icon && props.hasIconLeft" :icon="props.icon" :size="props.iconSize" />
      <slot />
      <BaseIcon v-if="props.icon && !props.hasIconLeft" :icon="props.icon" :size="props.iconSize" />
    </template>
    <component :is="skinComponent" v-if="skinComponent" />
    <Teleport v-if="props.isLoading && isMounted" :to="props.loadingOverlayOn">
      <div class="base-button__overlay" role="presentation" />
    </Teleport>
  </component>
</template>

<style lang="scss">
.base-button {
  position: relative;
  z-index: z-number(base);

  &--overlayed {
    z-index: z-number(base-button-overlay, 1);

    min-width: calc(v-bind(loadingButtonWidth)*1px);

    pointer-events: none;
  }

  &__loader {
    width: calc(v-bind(loadingButtonWidth)*1px);
  }

  &__overlay {
    position: absolute;
    z-index: z-number(base-button-overlay);

    background-color: $color-opacities-light;

    inset: 0;
  }
}
</style>
